/**
 * Wire shape of `/tiers.json` — the static, per-niche tier document this
 * brochure's own origin serves at the site root (`design/contracts.md`
 * "New: public tier source for the niche brochure", ratified 2026-08-12;
 * produced by duvi-niche-api + duvi-niche-business-www's publish worker,
 * story 155's dependency chain). `envelope.List`-shaped: a bare `data`
 * array plus an empty `meta` object — this document has no pagination (a
 * niche's whole tier catalogue, always).
 *
 * Every field on each row is ALWAYS present in a well-formed document —
 * never omitted (`design/contracts.md` house rule 3 / bug 0148: a consumer
 * doing `.map()` on a missing array crashes). `features` is always `[]`
 * today — there is no marketing-feature attribute on the stored tier item,
 * only numeric entitlement limits and AppConfig capabilities — see
 * `duvi-niche-site-tier-copy.ts` for how this brochure covers that gap.
 *
 * This document is deliberately NOT an OpenAPI contract (`design/
 * contracts.md`: "a static document, not an API"), but `duvi-contracts`
 * now carries a JSON Schema for it (`consumer-contracts/tiers-json.schema.json`,
 * generated type `DuviNicheTiersDocumentRow` in
 * `generated/typescript/tiers-document.ts`). The row type below mirrors
 * that generated type field-for-field rather than importing it, because the
 * generated package is not currently importable from this repo (bug 0213 —
 * gitignored output, no `exports`); `parseDuviNicheSiteTiersDocument` is
 * this repo's own runtime enforcement of that same schema before trusting
 * an unauthenticated static file fetched at runtime.
 */
export interface DuviNicheSiteTierDocumentRow {
  tier_id: string;
  /** The match key — what `?tier=` on the sign-up CTA must equal,
   * case-insensitively. Immutable; never render this. */
  name: string;
  /** The mutable, admin-edited label — render this on the card. */
  display_name: string;
  /** Pence, integer. First sort key, ascending. */
  price_pence: number;
  description: string;
  features: string[];
  /** RFC3339, fixed-width UTC seconds. Third sort key, ascending, after
   * `price_pence` then `name` — see `compareTierRows` in
   * `use-duvi-niche-site-pricing.ts`. */
  created_at: string;
  /**
   * readOnly, producer-derived: `presentation.highlighted_tier != null &&
   * tier_id == presentation.highlighted_tier` (design/contracts.md "The
   * highlighted tier and the default tier"). NOT a sort key and never
   * computed here — a tier list with no `true` row renders no ribbon; the
   * absence of a highlight is a real answer, not a fallback to elect one
   * (house rule 3, "consumers render, never derive"). Treated as `false`
   * when absent from a row rather than as malformed — see
   * `parseDuviNicheSiteTiersDocument` — so a document produced before this
   * field existed degrades to "nothing highlighted", not a parse failure.
   */
  is_highlighted: boolean;
}

export interface DuviNicheSiteTiersDocument {
  data: DuviNicheSiteTierDocumentRow[];
  meta: Record<string, never>;
}

// Narrows to the fields required on the wire — deliberately NOT the full
// `DuviNicheSiteTierDocumentRow`, since `is_highlighted` is not required
// (see `parseDuviNicheSiteTiersDocument`'s doc comment) and a predicate
// claiming the full type here would make every row's `is_highlighted`
// falsely "already known" before the default is applied below.
type RequiredTierDocumentFields = Omit<
  DuviNicheSiteTierDocumentRow,
  "is_highlighted"
>;

function isTierDocumentRow(
  value: unknown,
): value is RequiredTierDocumentFields & Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.tier_id === "string" &&
    typeof row.name === "string" &&
    typeof row.display_name === "string" &&
    typeof row.price_pence === "number" &&
    typeof row.description === "string" &&
    Array.isArray(row.features) &&
    typeof row.created_at === "string"
  );
}

/**
 * Validates an already-JSON-parsed `/tiers.json` body against the shape
 * above, returning its rows or `null` if the document is malformed —
 * absent, wrong-shaped, or a row missing a required field. Callers treat
 * `null` the same as a 404 or a network failure: an "unavailable" state,
 * never a crash (story 155's brief: "Handle in-flight, 404 and
 * parse-failure states without a blank or crashed page").
 *
 * `is_highlighted` is intentionally NOT part of the required-field check in
 * `isTierDocumentRow` above: a document produced before the field existed
 * (or a row where the producer omitted it) is still well-formed, just
 * unhighlighted. Every row here always carries the field, defaulted to
 * `false` when the wire value is absent or not a boolean.
 */
export function parseDuviNicheSiteTiersDocument(
  value: unknown,
): DuviNicheSiteTierDocumentRow[] | null {
  if (typeof value !== "object" || value === null) return null;
  const doc = value as Record<string, unknown>;
  if (!Array.isArray(doc.data)) return null;
  if (!doc.data.every(isTierDocumentRow)) return null;
  return doc.data.map((row) => ({
    tier_id: row.tier_id,
    name: row.name,
    display_name: row.display_name,
    price_pence: row.price_pence,
    description: row.description,
    features: row.features,
    created_at: row.created_at,
    is_highlighted:
      typeof row.is_highlighted === "boolean" ? row.is_highlighted : false,
  }));
}
